import { drawSpriteRect } from "./gl.js";
import {
  appState,
  rewardBackgroundSprite,
  rewardLoseSprite,
  rewardWinSprite,
  winOverlay,
} from "./state.js";
import { clamp, lerp } from "./utils.js";
import { playSfx } from "./sfx.js";

const OVERLAY_FADE_IN = 0.35;
const TEXT_BOUNCE_DURATION = 0.55;
const FLOAT_SPEED = 1.2;
const FLOAT_AMPLITUDE = 0.012;
const REWARD_BG_ALPHA = 1;
const TEXT_HEIGHT = 0.09375;

export function startWinOverlay(team, instant = false) {
  if (!team) {
    return;
  }
  winOverlay.active = true;
  winOverlay.team = team;
  winOverlay.elapsed = instant ? OVERLAY_FADE_IN : 0;
  winOverlay.textElapsed = instant ? TEXT_BOUNCE_DURATION : 0;
  winOverlay.floatTime = 0;
  playSfx(team === "player" ? "win_fanfare" : "lose_fanfare");
  appState.requestDraw?.();
}

export function isWinOverlayActive() {
  return winOverlay.active;
}

export function updateWinOverlay(dt) {
  if (!winOverlay.active) {
    return;
  }
  winOverlay.elapsed += dt;
  winOverlay.textElapsed += dt;
  winOverlay.floatTime += dt;
}

export function renderWinOverlay(panelRect) {
  if (!winOverlay.active) {
    return;
  }
  const alpha = clamp(winOverlay.elapsed / OVERLAY_FADE_IN, 0, 1);

  const textSprite = winOverlay.team === "player" ? rewardWinSprite : rewardLoseSprite;
  if (!textSprite.texture) {
    return;
  }
  const bounceT = clamp(winOverlay.textElapsed / TEXT_BOUNCE_DURATION, 0, 1);
  const scale = getBounceScale(bounceT);
  const floatOffset = Math.sin(winOverlay.floatTime * FLOAT_SPEED) * panelRect.height * FLOAT_AMPLITUDE;
  const textHeight = panelRect.height * TEXT_HEIGHT * scale;
  const size = getSpriteSize(textSprite);
  const textWidth = textHeight * (size.width / size.height);
  const center = {
    x: panelRect.left + panelRect.width * 0.5,
    y: panelRect.bottom + panelRect.height * 0.5 + floatOffset,
  };
  const rect = {
    left: center.x - textWidth * 0.5,
    bottom: center.y - textHeight * 0.5,
    width: textWidth,
    height: textHeight,
  };
  if (REWARD_BG_ALPHA > 0.001 && rewardBackgroundSprite.texture) {
    const bgRect = getRewardBackgroundRect(panelRect, textHeight * 1.2, center.y);
    drawSpriteRect(rewardBackgroundSprite, bgRect, [1, 1, 1, REWARD_BG_ALPHA]);
  }
  drawSpriteRect(textSprite, rect, [1, 1, 1, alpha]);
}

function getSpriteSize(sprite) {
  const width = sprite.sourceWidth || sprite.width || 1;
  const height = sprite.sourceHeight || sprite.height || 1;
  return { width, height };
}

function getRewardBackgroundRect(panelRect, targetHeight, centerY) {
  const height = Math.max(1, targetHeight);
  return {
    left: panelRect.left,
    bottom: centerY - height * 0.5,
    width: panelRect.width,
    height,
  };
}

function getBounceScale(t) {
  const clamped = clamp(t, 0, 1);
  const segment = clamped * 3;
  const index = Math.min(2, Math.floor(segment));
  const local = segment - index;
  const keys = [1.6, 0.92, 1.08, 1.0];
  return lerp(keys[index], keys[index + 1], local);
}
